import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import {
  InputSearchFilter,
  NgCommonModule,
  TypedFormControl,
  validFormControlChanges$,
} from '@principle-theorem/ng-shared';
import { ReportingReference } from '@principle-theorem/principle-core/interfaces';
import {
  CanBeChartedProperty,
  ReportBuilderDataPoint,
  ReportBuilderDataSourceInstance,
} from '@principle-theorem/reporting';
import { ISelectOption } from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface IDataPointOption extends ISelectOption<string> {
  icon: string;
  description: string;
}

interface IDataPointOptionGroup {
  label: string;
  options: IDataPointOption[];
}

@Component({
  selector: 'pr-datapoint-selector',
  templateUrl: './datapoint-selector.component.html',
  styleUrls: ['./datapoint-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIcon,
    MatIconButton,
    MatInput,
    MatTooltip,
    NgCommonModule,
    ReactiveFormsModule,
  ],
})
export class DatapointSelectorComponent {
  dataPointCtrl = new TypedFormControl<string | IDataPointOption>();

  label = input<string>('Select a Data Point');
  dataSource = input.required<ReportBuilderDataSourceInstance>();
  measures = input.required<CanBeChartedProperty[]>();
  datapointId = input<ReportingReference>();
  datapointIdChange = output<ReportingReference>();

  dataPoints = computed(() =>
    this.measures().map((column) => this._toOption(column))
  );
  filteredGroups$: Observable<IDataPointOptionGroup[]>;

  constructor() {
    effect(() => {
      const datapointId = this.datapointId();
      if (!datapointId) {
        return;
      }
      const option = this._findOption(this.dataPoints(), datapointId) ?? '';
      this.dataPointCtrl.setValue(option, { emitEvent: false });
    });

    const searchFilter = new InputSearchFilter<IDataPointOption>(
      toObservable(this.dataPoints),
      validFormControlChanges$(this.dataPointCtrl),
      ['label', 'description', 'value']
    );

    this.filteredGroups$ = combineLatest([
      toObservable(this.dataSource),
      searchFilter.results$,
    ]).pipe(
      map(([dataSource, results]) =>
        this._getFilteredGroups(dataSource, results)
      )
    );
  }

  clear(): void {
    this.dataPointCtrl.setValue('');
  }

  displayFn(option?: IDataPointOption): string {
    return option?.label ?? '';
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedOption = event.option.value as IDataPointOption;
    this.datapointIdChange.emit(selectedOption.value);
  }

  private _findOption(
    options: IDataPointOption[],
    datapointId: ReportingReference
  ): IDataPointOption | undefined {
    return options.find((option) => option.value === datapointId);
  }

  private _getFilteredGroups(
    dataSource: ReportBuilderDataSourceInstance,
    filteredDataPoints: IDataPointOption[]
  ): IDataPointOptionGroup[] {
    const groups = dataSource.availableColumns
      .map((section) => {
        const options = section.columns
          .map((column) => this._toOption(column.measure))
          .filter((option) =>
            filteredDataPoints.some(
              (datapoint) => datapoint.value === option.value
            )
          );

        return {
          label: section.name,
          options: sortBy(options, 'label'),
        };
      })
      .filter((group) => group.options.length > 0);

    return sortBy(groups, 'label');
  }

  private _toOption(measure: CanBeChartedProperty): IDataPointOption {
    return {
      label: ReportBuilderDataPoint.getLabel(measure),
      value: measure.metadata.id,
      icon: ReportBuilderDataPoint.getIcon(measure),
      description: ReportBuilderDataPoint.getDescription(measure),
    };
  }
}
